import classNames from "classnames";
import * as PropTypes from "prop-types";
import Waypoint from "react-waypoint";
import KeyHandler, { KEYPRESS } from "react-key-handler";

import stackedSliderStyles from "./StackedSlider.css";
import { BREAKPOINTS } from "/javascripts/constants/breakpoints";
import { KEYCODES } from "/javascripts/constants/keyCodes";

import { withViewport } from "/react/utils/withViewport";

import { StackedSliderControls } from "./StackedSliderControls";
import { StackedSliderSlide } from "./StackedSliderSlide";
import { StackedSliderFullscreen } from "./StackedSliderFullscreen";

@withViewport()
export class StackedSlider extends React.PureComponent {
  static propTypes = {
    children: PropTypes.node.isRequired,
    hideOnMobile: PropTypes.bool,
  };

  state = {
    activeSlideIndex: 0,
    areKeyHandlersEnabled: false,
    dragPositionDelta: 0,
    dragScale: 0,
    isDragging: false,
    isFullscreen: false,
    originalTotalSlides: 0,
    previousActiveSlideIndex: 0,
    slides: null,
    totalSlides: 0,
  };

  layoutConfig = {
    carousel: {
      0: {
        opacity: 0,
        positionDeltaMultiplier: 0,
        scale: 0.74,
        zIndex: 0,
      },
      1: {
        opacity: 0,
        positionDeltaMultiplier: -1,
        scale: 0.74,
        zIndex: 0,
      },
      2: {
        opacity: 1,
        positionDeltaMultiplier: -0.5,
        scale: 0.88,
        zIndex: 1,
      },
      3: {
        opacity: 1,
        positionDeltaMultiplier: 0,
        scale: 1,
        zIndex: 2,
      },
      4: {
        opacity: 1,
        positionDeltaMultiplier: 0.5,
        scale: 0.88,
        zIndex: 1,
      },
      5: {
        opacity: 0,
        positionDeltaMultiplier: 1,
        scale: 0.74,
        zIndex: 0,
      },
    },
    stacked: {
      0: {
        opacity: 0,
        positionDeltaMultiplier: 0,
        zIndex: 0,
      },
      1: {
        opacity: 0,
        positionDeltaMultiplier: -2,
        zIndex: 5,
      },
      2: {
        opacity: 0,
        positionDeltaMultiplier: -1,
        zIndex: 4,
      },
      3: {
        opacity: 1,
        positionDeltaMultiplier: 0,
        zIndex: 3,
      },
      4: {
        opacity: 1,
        positionDeltaMultiplier: 1,
        zIndex: 2,
      },
      5: {
        opacity: 1,
        positionDeltaMultiplier: 1,
        zIndex: 1,
      },
    },
  };

  activeSlideRef = React.createRef();
  activeSlideWidth = 0;
  dragPercentageForAction = 30;
  dragStart = 0;
  inViewportMargin = "25%";
  minimumAmountOfSlides = 5;

  hasCarouselLayout = () => this.props.viewportWidth < BREAKPOINTS.LAPTOP;

  hasStackedLayout = () => this.props.viewportWidth >= BREAKPOINTS.LAPTOP;

  getPreviousSlideIndex = (slideIndex) =>
    slideIndex === 0 ? this.state.totalSlides - 1 : slideIndex - 1;

  getNextSlideIndex = (slideIndex) =>
    slideIndex === this.state.totalSlides - 1 ? 0 : slideIndex + 1;

  getSlidePositionDelta = () => {
    if (this.hasStackedLayout())
      return parseInt(stackedSliderStyles.sliderSpaceStacked, 10);
    if (this.hasCarouselLayout())
      return parseInt(stackedSliderStyles.sliderSpaceCarousel, 10);

    return 0;
  };

  getSlidePositionInStack = (slideIndex) => {
    const activeSlideIndex = this.state.activeSlideIndex;

    if (
      slideIndex ===
      this.getPreviousSlideIndex(this.getPreviousSlideIndex(activeSlideIndex))
    ) {
      return 1;
    } else if (slideIndex === this.getPreviousSlideIndex(activeSlideIndex)) {
      return 2;
    } else if (slideIndex === activeSlideIndex) {
      return 3;
    } else if (slideIndex === this.getNextSlideIndex(activeSlideIndex)) {
      return 4;
    } else if (
      slideIndex ===
      this.getNextSlideIndex(this.getNextSlideIndex(activeSlideIndex))
    ) {
      return 5;
    }

    return 0;
  };

  // We need a minimum amount of slides to get the animated stacked effect. We keep copying the existing slides until
  // we have enough to create the stack.
  getCopiedSlides = (children) => {
    let slides = [];
    const components = [];

    // Create and fill a new array as React won't let us change the children object directly.
    React.Children.forEach(children, (child) => {
      components.push(child);
    });

    while (slides.length < this.minimumAmountOfSlides) {
      slides = [...slides, ...components];
    }

    return slides;
  };

  setSlideComponents = () => {
    const { children } = this.props;

    const totalChildren = React.Children.count(children);
    const slides =
      totalChildren > 1 && totalChildren < this.minimumAmountOfSlides
        ? this.getCopiedSlides(children)
        : React.Children.toArray(children);

    this.setState({
      slides,
      originalTotalSlides: totalChildren,
      totalSlides: slides.length,
    });
  };

  setKeyHandlers = () => {
    this.setState({ areKeyHandlersEnabled: true });
  };

  unsetKeyHandlers = () => {
    this.setState({ areKeyHandlersEnabled: false });
  };

  goToPreviousSlide = () => {
    this.setState((prevState) => {
      return {
        activeSlideIndex: this.getPreviousSlideIndex(
          prevState.activeSlideIndex
        ),
        isDragging: false,
        previousActiveSlideIndex: prevState.activeSlideIndex,
      };
    });
  };

  goToNextSlide = () => {
    this.setState((prevState) => {
      return {
        activeSlideIndex: this.getNextSlideIndex(prevState.activeSlideIndex),
        isDragging: false,
        previousActiveSlideIndex: prevState.activeSlideIndex,
      };
    });
  };

  toggleFullscreen = () => {
    this.setState((prevState) => {
      return {
        isFullscreen: !prevState.isFullscreen,
      };
    });
  };

  canDrag = (event) => {
    return (
      this.state.originalTotalSlides > 0 &&
      this.hasCarouselLayout() &&
      event.touches &&
      event.touches[0]
    );
  };

  handleDragStart = (event) => {
    if (!this.canDrag(event)) return;

    this.dragStart = event.touches[0].pageX;
    this.activeSlideWidth =
      this.activeSlideRef.current.getBoundingClientRect().width;
  };

  handleDragMove = (event) => {
    if (!this.canDrag(event)) return;

    const dragOffset = 0 - (this.dragStart - event.touches[0].pageX);
    const dragPercentage = (dragOffset / this.activeSlideWidth) * 100;
    const dragScale =
      1 - Math.abs(dragPercentage / this.dragPercentageForAction) * 0.06;
    const hasDraggedToNext = dragPercentage < 0 - this.dragPercentageForAction;
    const hasDraggedToPrevious = dragPercentage > this.dragPercentageForAction;

    if (hasDraggedToNext) {
      this.cancelDrag(event);
      this.goToNextSlide();
      return;
    }

    if (hasDraggedToPrevious) {
      this.cancelDrag(event);
      this.goToPreviousSlide();
      return;
    }

    this.setState({
      dragPositionDelta: dragPercentage,
      dragScale,
      isDragging: true,
    });
  };

  handleDragEnd = () => {
    this.setState({
      isDragging: false,
    });
  };

  cancelDrag = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  componentDidMount() {
    this.setSlideComponents();
  }

  render() {
    const {
      activeSlideIndex,
      areKeyHandlersEnabled,
      dragPositionDelta,
      dragScale,
      isDragging,
      isFullscreen,
      originalTotalSlides,
      previousActiveSlideIndex,
      slides,
    } = this.state;

    if (!slides || originalTotalSlides === 0) return null;

    const hasStackedLayout = this.hasStackedLayout();
    const slidePositionDelta = this.getSlidePositionDelta();
    const layoutConfig = hasStackedLayout
      ? this.layoutConfig.stacked
      : this.layoutConfig.carousel;
    const hasMultipleSlides = originalTotalSlides > 1;

    const multipleSlideProps = {
      activeSlideIndex,
      activeSlideRef: this.activeSlideRef,
      dragPositionDelta,
      dragScale,
      getSlidePositionInStack: this.getSlidePositionInStack,
      goToNextSlide: this.goToNextSlide,
      hasStackedLayout,
      isDragging,
      layoutConfig,
      onTouchStart: this.handleDragStart,
      onTouchMove: this.handleDragMove,
      onTouchEnd: this.handleDragEnd,
      slidePositionDelta,
      slides,
      toggleFullscreen: this.toggleFullscreen,
    };

    const singleSlideProps = {
      activeSlideRef: this.activeSlideRef,
      slide: slides[0],
    };

    const controlsProps = {
      activeSlide: slides[activeSlideIndex],
      activeSlideIndex,
      goToNextSlide: this.goToNextSlide,
      goToPreviousSlide: this.goToPreviousSlide,
      hasMultipleSlides,
      hasStackedLayout,
      originalTotalSlides,
      toggleFullscreen: this.toggleFullscreen,
      hideOnMobile: this.props.hideOnMobile,
    };

    const fullscreenProps = {
      activeSlide: slides[activeSlideIndex],
      activeSlideIndex,
      animationFrameTarget: this.activeSlideRef.current,
      getSlidePositionInStack: this.getSlidePositionInStack,
      goToNextSlide: this.goToNextSlide,
      goToPreviousSlide: this.goToPreviousSlide,
      originalTotalSlides,
      previousActiveSlideIndex,
      slides,
      toggleFullscreen: this.toggleFullscreen,
    };

    return (
      <React.Fragment>
        <Waypoint
          onEnter={this.setKeyHandlers}
          onLeave={this.unsetKeyHandlers}
          topOffset={this.inViewportMargin}
          bottomOffset={this.inViewportMargin}
        >
          <div
            className={classNames(stackedSliderStyles.slidesContainer, {
              [stackedSliderStyles.slidesContainerHasMultipleSlides]:
                hasMultipleSlides,
            })}
          >
            <div>
              {hasMultipleSlides ? (
                <MultipleSlides {...multipleSlideProps} />
              ) : (
                <SingleSlide {...singleSlideProps} />
              )}
              <StackedSliderControls {...controlsProps} />
              {isFullscreen && <StackedSliderFullscreen {...fullscreenProps} />}
            </div>
          </div>
        </Waypoint>
        {areKeyHandlersEnabled && (
          <React.Fragment>
            <KeyHandler
              keyEventname={KEYPRESS}
              code={KEYCODES.LEFT}
              onKeyHandle={this.goToPreviousSlide}
            />
            <KeyHandler
              keyEventname={KEYPRESS}
              code={KEYCODES.RIGHT}
              onKeyHandle={this.goToNextSlide}
            />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}
const MultipleSlides = (props) =>
  props.slides.map((slide, slideIndex) => {
    const positionInStack = props.getSlidePositionInStack(slideIndex);

    const motionStyles = {
      positionDelta: props.hasStackedLayout
        ? props.layoutConfig[positionInStack].positionDeltaMultiplier *
          props.slidePositionDelta
        : props.layoutConfig[positionInStack].positionDeltaMultiplier * 100,
      opacity: props.layoutConfig[positionInStack].opacity,
      scale: props.hasStackedLayout
        ? 1
        : props.layoutConfig[positionInStack].scale,
      zIndex: props.layoutConfig[positionInStack].zIndex,
    };

    const slideProps = {
      goToNextSlide: props.goToNextSlide,
      hasStackedLayout: props.hasStackedLayout,
      key: slideIndex,
      motionStyles,
      positionInStack,
      slide,
    };

    if (slideIndex === props.activeSlideIndex) {
      slideProps.activeSlideRef = props.activeSlideRef;
      slideProps.onTouchStart = props.onTouchStart;
      slideProps.onTouchMove = props.onTouchMove;
      slideProps.onTouchEnd = props.onTouchEnd;

      if (props.isDragging && slideIndex) {
        slideProps.motionStyles.positionDelta = props.dragPositionDelta;
        slideProps.motionStyles.scale = props.dragScale;
      }
    }

    if (positionInStack === 4 && props.hasStackedLayout) {
      slideProps.onClick = props.goToNextSlide;
    }

    return <StackedSliderSlide {...slideProps} />;
  });

const SingleSlide = (props) => (
  <div className={stackedSliderStyles.slide} ref={props.activeSlideRef}>
    {props.slide}
  </div>
);
